import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import type { FetcherWithComponents } from 'react-router';
import { AdyenCheckout, Dropin, type UIElement, Card } from '@adyen/adyen-web';
import '@adyen/adyen-web/styles/adyen.css';
import { type Component, loadCheckoutWebComponents } from '@checkout.com/checkout-web-components';
import type {
  CardPaymentInstrument,
  CkoPaymentSession,
  ClientSupportedPaymentMethod,
} from '@dinewise/graphql/types';
import { captureClientException } from '@dinewise/logger/client';
import { zfd } from '@dinewise/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { ValidatedForm, withZod } from '@michaelgmcd/rvf';
import { useRemixForm } from 'remix-hook-form';
import * as z from 'zod';
import { ActivityIndicator } from './activity-indicator.tsx';
import { Button } from './buttons/index.tsx';
import { HiddenInput } from './fields/hidden-input.tsx';
import { Input } from './fields/input.tsx';
import { RHFInput } from './fields/rhf-input.tsx';
import { finixPaymentFormErrorMessage } from './finix-payment-form-error-message.ts';
import { ModalOverlay } from './modal.tsx';
import { Notification, type NotificationType } from './notification/index.tsx';
import {
  buildPaymentCardSessionI18n,
  defaultPaymentCheckoutFlowLabels,
  paymentCardSessionI18nSignature,
  type PaymentCheckoutFlowLabels,
} from './payment-method-checkout-translations.ts';
import {
  AddPaymentMethodForm,
  accountCategories,
  type PaymentForm,
  type PaymentFormSubmitHandler,
} from './payment-method-form.tsx';
import type { PaymentMethodsFetchErrorLabels } from './payment-methods-fetch-error.tsx';

const accountCategorySchema = z.object({
  accountCategory: zfd.text(z.enum(['personal', 'corporate'])),
  reservationScope: zfd.text(z.string().optional()),
});

const accountCategoryValidator = withZod(accountCategorySchema);

export const editPaymentMethodValidator = withZod(
  z.object({
    ...accountCategorySchema.shape,
    paymentInstrumentId: zfd.text(),
    city: zfd.text(),
    region: zfd.text(),
    postalCode: zfd.text(),
    line1: zfd.text(),
    line2: zfd.text(z.string().optional()),
    country: zfd.text(),
    identity: zfd.text(),
  }),
);

export type PartialCardPaymentInstrument = Pick<
  CardPaymentInstrument,
  | '__typename'
  | 'id'
  | 'brand'
  | 'lastFour'
  | 'expirationMonth'
  | 'expirationYear'
  | 'tags'
  | 'address'
>;

type SharedAddPaymentMethodModalProps = {
  environment: 'sandbox' | 'live';
  applicationId: string;
  isModalOpen: boolean;
  ckoPaymentSession?: CkoPaymentSession;
  fetcher: FetcherWithComponents<unknown>;
  supportedMethods: Array<'CREDIT_CARD' | 'ACH'>;
  paymentIdentityId: string | null;
  userId?: string | null;
  defaultAccountCategory: keyof typeof accountCategories;
  onCloseHandler: (paymentId?: string) => void;
  actionRoute?: string;
  reservationScope?: string;
  customText?: {
    title?: string;
    submitButtonText?: string;
  };
  setAsDefault?: boolean;
  showBackButton?: boolean;
  showCloseButton?: boolean;
  onBack?: () => void;
  modalClassNames?: {
    panel?: string;
    panelContainer?: string;
    body?: string;
    footer?: string;
  };
  infoBanner?: ReactNode;
  labels?: PaymentMethodModalLabels;
};

type FinixAddPaymentMethodModalProps = {
  provider: 'FINIX';
};

type CheckoutAddPaymentMethodModalProps = {
  provider: 'CHECKOUT';
  ckoPaymentSession: CkoPaymentSession;
};

type AdyenAddPaymentMethodModalProps = {
  provider: 'ADYEN';
  ckoPaymentSession: CkoPaymentSession;
};

type AddPaymentMethodModalProps = SharedAddPaymentMethodModalProps &
  (
    | FinixAddPaymentMethodModalProps
    | CheckoutAddPaymentMethodModalProps
    | AdyenAddPaymentMethodModalProps
  );

const isCardSessionProvider = (
  provider: AddPaymentMethodModalProps['provider'],
): provider is 'CHECKOUT' | 'ADYEN' => provider === 'CHECKOUT' || provider === 'ADYEN';

const adyenSuccessResultCodes = new Set(['Authorised', 'Pending', 'Received']);

export type PaymentMethodModalLabels = {
  close: string;
  addTitle: string;
  creating: string;
  continue: string;
  saveCard: string;
  saving: string;
  saved: string;
  save: string;
  cancel: string;
  addCard: string;
  addPaymentMethod: string;
  zipCode: string;
  zipCodePlaceholder: string;
  zipCodeRequired: string;
  zipCodeTooLong: string;
  personal: string;
  corporate: string;
  fullName: string;
  routingNumber: string;
  expirationPlaceholder: string;
  addErrorTitle: string;
  unknownError: string;
  loadFormErrorTitle: string;
  loadFormErrorBody: string;
  editTitle: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  zip: string;
  country: string;
  onFileTitle: string;
  fetchError: PaymentMethodsFetchErrorLabels;
  viewerLocale: string;
  checkoutFlow: PaymentCheckoutFlowLabels;
};

const defaultPaymentMethodModalLabels: PaymentMethodModalLabels = {
  close: 'Close',
  addTitle: 'Add New Payment Method',
  creating: 'Creating...',
  continue: 'Continue',
  saveCard: 'Save Card',
  saving: 'Saving...',
  saved: 'Saved',
  save: 'Save',
  cancel: 'Cancel',
  addCard: 'Add Card',
  addPaymentMethod: 'Add Payment Method',
  zipCode: 'ZIP Code',
  zipCodePlaceholder: 'Enter ZIP Code',
  zipCodeRequired: 'Valid ZIP Code is Required',
  zipCodeTooLong: 'ZIP Code is too long',
  personal: 'Personal',
  corporate: 'Corporate',
  fullName: 'Full Name',
  routingNumber: 'Routing Number',
  expirationPlaceholder: 'MM/YY',
  addErrorTitle: 'Error adding payment method',
  unknownError: 'Unknown Error',
  loadFormErrorTitle: 'Unable to load payment form',
  loadFormErrorBody: 'Check your connection, then close and reopen the payment form to try again.',
  editTitle: 'Edit Payment Method',
  addressLine1: 'Address Line 1',
  addressLine2: 'Address Line 2',
  city: 'City',
  state: 'State',
  zip: 'ZIP',
  country: 'Country',
  onFileTitle: 'Edit Payment Method on File',
  fetchError: {
    message: 'There was an error trying to fetch your available cards.\nPlease try reloading them.',
    reload: 'Reload Card List',
  },
  viewerLocale: 'en-US',
  checkoutFlow: defaultPaymentCheckoutFlowLabels,
};

export type AddPaymentMethodFetcherProps = Pick<
  AddPaymentMethodModalProps,
  'applicationId' | 'environment' | 'userId' | 'paymentIdentityId'
> & { clientPaymentMethods: ClientSupportedPaymentMethod[] };

type EditPaymentMethodModalProps = Pick<
  AddPaymentMethodModalProps,
  'isModalOpen' | 'fetcher' | 'paymentIdentityId' | 'onCloseHandler'
> & {
  selectedPaymentInstrument?: PartialCardPaymentInstrument;
  labels?: PaymentMethodModalLabels;
};

export type AddPaymentMethodModalHandle = {
  // allow parent to toggle modal, while maintaining modal state internally
  toggleModal: () => void;
};

export const AddPaymentMethodModal = ({
  environment,
  applicationId,
  isModalOpen: isAddPaymentMethodModalOpen,
  fetcher,
  supportedMethods,
  defaultAccountCategory,
  paymentIdentityId,
  userId,
  onCloseHandler: onClose,
  actionRoute: action,
  reservationScope,
  customText,
  setAsDefault,
  showBackButton,
  showCloseButton,
  onBack,
  modalClassNames,
  infoBanner,
  labels: labelsProp,
  ...providerProps
}: AddPaymentMethodModalProps) => {
  const labels = labelsProp ?? defaultPaymentMethodModalLabels;
  const cardSessionI18nInput = {
    viewerLocale: labels.viewerLocale,
    checkoutFlow: labels.checkoutFlow,
    saveCard: labels.saveCard,
    saving: labels.saving,
    saved: labels.saved,
  };
  const cardSessionI18nKey = paymentCardSessionI18nSignature(cardSessionI18nInput);
  const finixFormRef = useRef<PaymentForm | null>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const isSubmitting = fetcher.state === 'submitting' || fetcher.state === 'loading';

  const [finixFormLoaded, setFinixFormLoaded] = useState(false);
  const [finixFormIsValid, setFinixFormIsValid] = useState(false);
  const [sessionFormKey, setSessionFormKey] = useState<null | string>(null);
  const [sessionFormSubmitting, setSessionFormSubmitting] = useState(false);
  const [checkoutForm, setCheckoutForm] = useState<Component | null>(null);
  const [adyenDropin, setAdyenDropin] = useState<UIElement | null>(null);
  const [unmountModal, setUnmountModal] = useState(true);
  const [notification, setNotification] = useState<NotificationType | null>(null);
  const [isModalOpen, setModalOpen] = useState(isAddPaymentMethodModalOpen);
  const [sessionFormIsValid, setSessionFormIsValid] = useState(false);
  const { setError, watch, control, trigger, reset } = useRemixForm({
    mode: 'onChange',
    resolver: zodResolver(
      z.object({
        zipCode: zfd.text(
          z.string({ error: labels.zipCodeRequired }).max(30, { message: labels.zipCodeTooLong }),
        ),
      }),
    ),
  });

  const loadCkoComponentsRef = useRef<boolean>(false);
  const loadAdyenComponentsRef = useRef<boolean>(false);
  const adyenDropinContainerRef = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const resetRef = useRef(reset);
  resetRef.current = reset;
  const zipCodeFieldValue = watch('zipCode');
  const finixSubmitHandler = useCallback<PaymentFormSubmitHandler>(
    (err, res) => {
      if (err) {
        const providerMessage = finixPaymentFormErrorMessage(res);
        setNotification({
          type: 'error',
          title: labels.addErrorTitle,
          body: providerMessage ?? labels.unknownError,
          time: Date.now(),
        });
        const errToCapture =
          err instanceof Error
            ? err
            : typeof err === 'string'
              ? new Error(err)
              : new Error('There was an issue submitting the Finix Payment Form');

        captureClientException({
          error: errToCapture,
        });
      } else {
        const tokenData = res?.data ?? null;
        if (tokenData) {
          const token = tokenData.id;
          if (!formRef.current) {
            return;
          }
          const formData = new FormData(formRef.current);
          const category = formData.get('accountCategory');
          if (!category || typeof category !== 'string') {
            return;
          }
          if (paymentIdentityId) {
            fetcher.submit(
              {
                subaction: 'createPaymentInstrument',
                token,
                identity: paymentIdentityId,
                accountCategory: category,
                reservationScope: reservationScope || '',
              },
              {
                method: 'POST',
                ...(action ? { action } : {}),
              },
            );
          } else {
            fetcher.submit(
              {
                subaction: 'createIdentityAndPaymentInstrument',
                token,
                userId: userId ?? '',
                accountCategory: category.toString(),
                reservationScope: reservationScope || '',
              },
              { method: 'POST', ...(action ? { action } : {}) },
            );
          }
        }
      }
    },
    [
      fetcher,
      userId,
      paymentIdentityId,
      action,
      reservationScope,
      labels.addErrorTitle,
      labels.unknownError,
    ],
  );

  // oxlint-disable-next-line react-doctor/no-derived-state-effect -- intentionally syncs external inputs into local UI or form state
  useEffect(() => {
    setModalOpen(isAddPaymentMethodModalOpen);
  }, [isAddPaymentMethodModalOpen]);

  const handleCreateSessionInstrumentSubmit = async () => {
    const isValid = await trigger('zipCode');

    if (!isValid) {
      setError('zipCode', { message: labels.zipCodeRequired });

      return;
    }

    if (providerProps.provider === 'CHECKOUT') {
      checkoutForm?.submit();

      return;
    }

    if (providerProps.provider === 'ADYEN') {
      setSessionFormSubmitting(true);
      adyenDropin?.submit();
    }
  };

  const submitInstrumentFromPayment = useCallback(
    (paymentProviderToken: string) => {
      setUnmountModal(false);
      setSessionFormSubmitting(false);
      const zipCode = watch('zipCode');
      if (paymentProviderToken && zipCode) {
        fetcher.submit(
          {
            subaction: 'addInstrumentFromPayment',
            paymentProviderToken,
            identity: paymentIdentityId ?? '',
            reservationScope: reservationScope ?? '',
            zipCode,
            ...(setAsDefault ? { setAsDefault: 'true' } : {}),
          },
          { method: 'POST', ...(action ? { action } : {}) },
        );
      }
      reset({ zipCode: '' });
      onClose(paymentProviderToken);
    },
    [watch, fetcher, paymentIdentityId, reservationScope, setAsDefault, action, reset, onClose],
  );

  useEffect(() => {
    if (
      !isModalOpen ||
      sessionFormKey === providerProps.ckoPaymentSession?.id ||
      providerProps.provider !== 'CHECKOUT' ||
      loadCkoComponentsRef.current
    ) {
      return;
    }

    const onReady = () => {
      setSessionFormKey(providerProps.ckoPaymentSession?.id || null);
    };

    const mountCheckoutForm = async () => {
      try {
        const { publicKey } = providerProps.ckoPaymentSession;
        const paymentSessionI18n = buildPaymentCardSessionI18n(cardSessionI18nInput);
        const checkout = await loadCheckoutWebComponents({
          publicKey,
          environment: environment === 'live' ? 'production' : 'sandbox',
          locale: paymentSessionI18n.locale,
          appearance: {
            colorBorder: '#ffffff',
            colorAction: '#262626',
            colorPrimary: '#4f5d6e',
          },
          componentOptions: {
            flow: { showPayButton: false },
          },
          translations: {
            [paymentSessionI18n.translationLanguage]: paymentSessionI18n.fieldTranslations,
          },
          paymentSession: {
            id: providerProps.ckoPaymentSession.id,
            payment_session_secret: providerProps.ckoPaymentSession.paymentSessionSecret,
            payment_session_token: providerProps.ckoPaymentSession.paymentSessionToken,
          },
          onReady,
          onSubmit: () => {
            setSessionFormSubmitting(true);
          },
          onPaymentCompleted: (_component, paymentResponse) => {
            if (paymentResponse.id) {
              submitInstrumentFromPayment(paymentResponse.id);
            }
          },
          onError: (_component, error) => {
            setSessionFormSubmitting(false);
            captureClientException({ error, message: 'Failed to load Checkout form' });
          },
        });
        const flowComponent = checkout.create('flow', {
          onChange: component => {
            setSessionFormIsValid(component.isValid());
          },
        });

        flowComponent.mount('#checkout-form');
        setCheckoutForm(flowComponent);
      } catch (error) {
        loadCkoComponentsRef.current = false;
        setSessionFormSubmitting(false);
        setNotification({
          type: 'error',
          title: labels.loadFormErrorTitle,
          body: labels.loadFormErrorBody,
          time: Date.now(),
        });
        captureClientException({ error, message: 'Failed to load Checkout Web Components' });
      }
    };

    loadCkoComponentsRef.current = true;
    void mountCheckoutForm();
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- cardSessionI18nKey fingerprints cardSessionI18nInput for SDK remounts
  }, [
    isModalOpen,
    providerProps.provider,
    providerProps.ckoPaymentSession,
    sessionFormKey,
    environment,
    submitInstrumentFromPayment,
    cardSessionI18nKey,
    labels.loadFormErrorTitle,
    labels.loadFormErrorBody,
  ]);

  useEffect(() => {
    if (!isModalOpen) {
      loadAdyenComponentsRef.current = false;
      loadCkoComponentsRef.current = false;
      setSessionFormKey(null);
    }
  }, [isModalOpen]);

  const prevCardSessionI18nKeyRef = useRef(cardSessionI18nKey);
  useEffect(() => {
    if (prevCardSessionI18nKeyRef.current === cardSessionI18nKey) {
      return;
    }
    prevCardSessionI18nKeyRef.current = cardSessionI18nKey;
    if (providerProps.provider !== 'CHECKOUT') {
      return;
    }
    checkoutForm?.unmount();
    setCheckoutForm(null);
    loadCkoComponentsRef.current = false;
    setSessionFormKey(null);
    setSessionFormSubmitting(false);
  }, [cardSessionI18nKey, providerProps.provider, checkoutForm]);

  useEffect(() => {
    if (
      !isModalOpen ||
      providerProps.provider !== 'ADYEN' ||
      !providerProps.ckoPaymentSession ||
      loadAdyenComponentsRef.current
    ) {
      return;
    }

    let dropinComponent: UIElement | null = null;
    let ignore = false;
    const { publicKey, id, paymentSessionSecret } = providerProps.ckoPaymentSession;

    const mountAdyenDropin = async () => {
      try {
        const paymentSessionI18n = buildPaymentCardSessionI18n(cardSessionI18nInput);
        const checkout = await AdyenCheckout({
          clientKey: publicKey,
          environment: environment === 'live' ? 'live-us' : 'test',
          locale: paymentSessionI18n.locale,
          translations: {
            [paymentSessionI18n.translationLanguage]: {
              ...paymentSessionI18n.fieldTranslations,
              'pay_button.confirm': labels.addCard,
            },
          },
          session: {
            id,
            sessionData: paymentSessionSecret,
          },
          onChange: state => {
            setSessionFormIsValid(state.isValid);
          },
          onPaymentCompleted: result => {
            const resultCode = 'resultCode' in result ? result.resultCode : null;
            if (!resultCode || !adyenSuccessResultCodes.has(resultCode)) {
              setSessionFormSubmitting(false);

              return;
            }

            setUnmountModal(false);
            setSessionFormSubmitting(false);
            resetRef.current({ zipCode: '' });
            onCloseRef.current(id);
          },
          onPaymentFailed: error => {
            captureClientException({ error, message: 'Adyen Drop-in payment failed' });
            setSessionFormSubmitting(false);
          },
          onError: error => {
            setSessionFormSubmitting(false);
            captureClientException({ error, message: 'Failed to load Adyen Drop-in form' });
          },
        });

        if (ignore) {
          return;
        }

        const container = adyenDropinContainerRef.current;
        if (!container) {
          return;
        }

        dropinComponent = new Dropin(checkout, {
          showPayButton: false,
          paymentMethodComponents: [Card],
        });
        dropinComponent.mount(container);
        setAdyenDropin(dropinComponent);
        setSessionFormKey(id);
        loadAdyenComponentsRef.current = true;
      } catch (error) {
        captureClientException({ error, message: 'Failed to initialize Adyen Drop-in' });
      }
    };

    void mountAdyenDropin();

    return () => {
      ignore = true;
      dropinComponent?.unmount();
      loadAdyenComponentsRef.current = false;
    };
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- remount only when modal/session id/environment changes; full session object is unstable across renders
  }, [
    isModalOpen,
    providerProps.provider,
    providerProps.ckoPaymentSession?.id,
    environment,
    labels.addCard,
    cardSessionI18nKey,
  ]);

  return (
    <>
      <Notification
        notification={notification}
        closeLabel={labels.close}
        onHidden={() => {
          setNotification(null);
        }}
      />
      <ModalOverlay
        {...{ showBackButton, showCloseButton }}
        closeLabel={labels.close}
        title={customText?.title || labels.addTitle}
        visible={isModalOpen}
        unmount={unmountModal}
        onClose={() => {
          if (onBack) {
            onBack();
          } else {
            onClose();
            reset({ zipCode: '' });
          }
        }}
        classNames={{ panelContainer: 'py-4', ...modalClassNames }}
        footer={
          <div className="flex w-full flex-row justify-end">
            {providerProps.provider === 'FINIX' ? (
              <Button
                type="button"
                theme="primary"
                size="md"
                disabled={!finixFormLoaded || !finixFormIsValid || isSubmitting}
                onClick={() => {
                  if (!finixFormRef.current) {
                    return;
                  }
                  finixFormRef.current.submit(environment, applicationId, finixSubmitHandler);
                }}
              >
                {isSubmitting ? labels.creating : labels.continue}
              </Button>
            ) : providerProps.provider === 'CHECKOUT' ? (
              <Button
                theme="primary"
                size="md"
                className="mt-6 h-10 flex-1 pr-4"
                disabled={sessionFormSubmitting || !sessionFormIsValid || !zipCodeFieldValue}
                onClick={() => {
                  void handleCreateSessionInstrumentSubmit();
                }}
              >
                {sessionFormSubmitting ? (
                  <ActivityIndicator classNames={{ spinner: 'size-4' }} theme="spinner" />
                ) : (
                  customText?.submitButtonText || labels.saveCard
                )}
              </Button>
            ) : null}
          </div>
        }
        body={
          <div className="flex w-full flex-col items-center justify-center gap-2">
            {infoBanner}
            {providerProps.provider === 'FINIX' && (
              <div className="w-full">
                <ValidatedForm
                  formRef={formRef}
                  validator={accountCategoryValidator}
                  className="flex gap-4 border-gray-300 border-b-2 p-4"
                >
                  {Object.keys(accountCategories).map(key => {
                    const value = key as keyof typeof accountCategories;

                    return (
                      <div key={key} className="flex flex-col gap-2">
                        <Input
                          type="radio"
                          id={key}
                          name="accountCategory"
                          value={value}
                          label={value === 'personal' ? labels.personal : labels.corporate}
                          defaultChecked={defaultAccountCategory === value}
                          classNames={{ group: 'flex-row-reverse' }}
                        />
                      </div>
                    );
                  })}
                  {reservationScope && (
                    <HiddenInput name="reservationScope" value={reservationScope} />
                  )}
                </ValidatedForm>
              </div>
            )}

            <div className="flex w-full flex-col gap-2">
              {isCardSessionProvider(providerProps.provider) ? (
                <div className="flex size-full min-h-[436px] flex-col items-center justify-center gap-2">
                  {providerProps.provider === 'CHECKOUT' ? (
                    <div id="checkout-form" className="-mt-12" />
                  ) : (
                    <div ref={adyenDropinContainerRef} className="w-full" />
                  )}

                  {providerProps.provider === 'CHECKOUT' ? (
                    sessionFormKey ? (
                      <>
                        {' '}
                        <div className="-mt-11 z-10 flex h-14 w-full flex-row gap-3 pr-6 pl-6">
                          <RHFInput
                            classNames={{ group: 'w-[272px]', input: 'pl-4 h-12' }}
                            name="zipCode"
                            label={labels.zipCode}
                            placeholder={labels.zipCodePlaceholder}
                            disabled={sessionFormSubmitting}
                            control={control}
                          />
                        </div>
                      </>
                    ) : (
                      <div className="flex size-full items-center justify-center">
                        <ActivityIndicator
                          theme="spinner-gradient"
                          classNames={{ spinnerGradient: 'size-10' }}
                        />
                      </div>
                    )
                  ) : null}
                </div>
              ) : (
                <AddPaymentMethodForm
                  {...{ supportedMethods, setFinixFormIsValid, setFinixFormLoaded }}
                  formRef={finixFormRef}
                  showAddress
                  formLabels={{
                    fullName: labels.fullName,
                    routingNumber: labels.routingNumber,
                    expirationPlaceholder: labels.expirationPlaceholder,
                  }}
                />
              )}
            </div>
          </div>
        }
      />
    </>
  );
};

export const EditPaymentMethodModal = ({
  isModalOpen: isAddPaymentMethodModalOpen,
  fetcher,
  paymentIdentityId,
  onCloseHandler: onClose,
  selectedPaymentInstrument,
  labels: labelsProp,
}: EditPaymentMethodModalProps) => {
  const labels = labelsProp ?? defaultPaymentMethodModalLabels;
  const formRef = useRef<HTMLFormElement>(null);
  const isSubmitting = fetcher.state === 'submitting' || fetcher.state === 'loading';

  const [unmountModal, setUnmountModal] = useState(true);
  const [isModalOpen, setModalOpen] = useState(isAddPaymentMethodModalOpen);

  // oxlint-disable-next-line react-doctor/no-derived-state-effect -- intentionally syncs external inputs into local UI or form state
  useEffect(() => {
    setModalOpen(isAddPaymentMethodModalOpen);
  }, [isAddPaymentMethodModalOpen]);

  return (
    <ModalOverlay
      closeLabel={labels.close}
      title={labels.editTitle}
      visible={isModalOpen}
      unmount={unmountModal}
      onClose={() => {
        setUnmountModal(false);
        onClose();
      }}
      footer={
        <div className="flex w-full flex-row justify-end">
          <Button
            type="button"
            theme="primary"
            size="md"
            disabled={isSubmitting}
            onClick={() => {
              if (!formRef.current) {
                return;
              }
              fetcher.submit(formRef.current, { method: 'post', action: '/edit-payment-method' });
            }}
          >
            {isSubmitting ? labels.saving : labels.save}
          </Button>
        </div>
      }
      body={
        <div className="flex w-full flex-col items-center justify-center gap-2">
          <div className="w-full">
            <ValidatedForm
              formRef={formRef}
              validator={editPaymentMethodValidator}
              defaultValues={{ ...selectedPaymentInstrument?.address }}
              className="flex flex-col gap-4 p-4"
            >
              <HiddenInput name="paymentInstrumentId" value={selectedPaymentInstrument?.id} />
              <HiddenInput name="identity" value={paymentIdentityId ?? ''} />
              <HiddenInput
                name="accountCategory"
                value={selectedPaymentInstrument?.tags?.accountCategory}
              />
              <div className="flex w-full flex-col gap-2">
                <Input name="line1" label={labels.addressLine1} />
                <Input name="line2" label={labels.addressLine2} />
              </div>
              <div className="flex w-full gap-2">
                <Input name="city" classNames={{ group: 'w-full' }} label={labels.city} />
                <Input name="region" classNames={{ group: 'w-full' }} label={labels.state} />
              </div>
              <div className="flex w-full gap-2">
                <Input name="postalCode" classNames={{ group: 'w-full' }} label={labels.zip} />
                <Input name="country" classNames={{ group: 'w-full' }} label={labels.country} />
              </div>
            </ValidatedForm>
          </div>
        </div>
      }
    />
  );
};
